import React, {useEffect, useMemo, useState} from "react";
import ErrorIcon from "@mui/icons-material/Error";
import {Box} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {getDatasetStructureUrl, getDatasetUrl} from "../../constants/apiEndpoints";
import Call from "../../hocs/call";
import CustomEmpty from "../custom-empty";
import {getViewerIdxFromType, isChartLayoutCartesianByType} from "../dataset-handler/constant";
import DatasetStaticFiltersJsonStatWrapper from "../dataset-static-filters/DatasetStaticFiltersJsonStatWrapper";
import {TEMPORAL_DIM_ORDER_SELECTOR_VALUE_DESC} from "../temporal-dim-order-selector/constants";
import ChartJsonStatWrapper from "../viewer-stat-chart/ChartJsonStatWrapper";
import Map from "../viewer-stat-map/single-viewer/Map";
import Table from "../viewer-stat-table/single-viewer";
import useApi from "../../state/api/useApi";
import {languageSelector, languagesSelector} from "../../state/app/appSelectors";
import {ViewerMode} from "../../state/dataset/constants";
import {hubNodesSelector} from "../../state/hub/hubSelectors";
import {initRequest, RequestMethod} from "../../middlewares/request/requestActions";
import {getCriteriaArrayFromObject, getCriteriaObjectFromArray} from "../../utils/criteria";
import {generateLocalizedTimePeriodFormatMapFromExtras} from "../../utils/dataset";
import EXTRAS from "../../utils/extras";
import {localizeI18nObj} from "../../utils/i18n";
import {getChartSettingsFromViewTemplateLayouts, getMapSettingsFromViewTemplateLayouts} from "../../utils/viewTemplate";

const $ = window.jQuery;

const handleStyle = () => {
  const filtersHeight = $("#stat-viewer__filters").outerHeight(true) || 0;
  $("#stat-viewer__viewer").height(`calc(100% - ${filtersHeight}px)`);
};

const getNodeExtras = (nodes, nodeId, language, languages) => {
  const node = nodes?.find(node => node.nodeId === nodeId);
  const nodeExtras = EXTRAS.parse(node.extras, language, languages);
  return nodeExtras;
};

const StatViewer = ({nodeId, packageId, datasetId, viewId}) => {
  const {t} = useTranslation();

  const language = useSelector(languageSelector);
  const languages = useSelector(languagesSelector);

  const nodes = useSelector(hubNodesSelector);

  const [error, setError] = useState(false);

  const [view, setView] = useState(null);
  const [layoutObj, setLayoutObj] = useState({});

  const nodeExtras = useMemo(
    () => getNodeExtras(nodes, nodeId, language, languages),
    [nodes, nodeId, language, languages]
  );
  const localizedTimePeriodFormatMap = useMemo(
    () => generateLocalizedTimePeriodFormatMapFromExtras(nodeExtras),
    [nodeExtras]
  );

  const {call: fetchStructure} = useApi(
    initRequest(
      "stat-viewer/structure/fetch",
      getDatasetStructureUrl(nodeId, packageId, datasetId, viewId),
      RequestMethod.GET,
      undefined,
      t => ({
        onStart: t("components.statViewer.messages.fetchingStructure.start")
      })
    ),
    {
      clearOnUnmount: true,
      onSuccess: structure => {
        if (!structure || !structure.view || (structure.view.mode && structure.view.mode !== ViewerMode.SingleViewer)) {
          setError(true);
          return;
        }

        const timeDim = structure.timeDimension;

        const view = structure.view;

        const layoutObj = JSON.parse(view.layouts);

        let layout = {};
        if (view.defaultView === "table") {
          layout = layoutObj.tableLayout;
        } else if (view.defaultView === "map") {
          layout = layoutObj.mapLayout;
        } else {
          if (isChartLayoutCartesianByType(view.defaultView)) {
            if (layoutObj.chartLayoutCartesian) {
              layout = layoutObj.chartLayoutCartesian;
            } else {
              layout = layoutObj.chartLayout;
            }
          } else {
            if (layoutObj.chartLayoutRadial) {
              layout = layoutObj.chartLayoutRadial;
            } else {
              layout = layoutObj.chartLayout;
            }
          }
        }

        const criteria = getCriteriaObjectFromArray(view.criteria);
        Object.keys(layout.filtersValue).forEach(key => {
          if (key !== timeDim) {
            criteria[key] = {
              id: key,
              filterValues: [layout.filtersValue[key]]
            };
          }
        });
        const primaryDim = layout?.primaryDim?.[0] || null;
        if (primaryDim && primaryDim !== timeDim) {
          criteria[primaryDim] = {
            id: primaryDim,
            filterValues: layout.primaryDimValues
          };
        }
        const secondaryDim = layout?.secondaryDim?.[0] || null;
        if (secondaryDim && secondaryDim !== timeDim) {
          criteria[secondaryDim] = {
            id: secondaryDim,
            filterValues: layout.secondaryDimValues
          };
        }

        setView({
          ...view,
          title: localizeI18nObj(view?.title, language, languages),
          criteria: getCriteriaArrayFromObject(criteria),
          decimalSeparator: localizeI18nObj(view.decimalSeparator, language, languages)
        });

        setLayoutObj({
          ...layoutObj,
          layout: layout,
          chartSettings: getChartSettingsFromViewTemplateLayouts(layoutObj),
          mapSettings: getMapSettingsFromViewTemplateLayouts(layoutObj),
          invertedDims:
            layoutObj?.temporalDimOrder === TEMPORAL_DIM_ORDER_SELECTOR_VALUE_DESC &&
            !(layout?.filters || []).includes(timeDim)
              ? [timeDim]
              : null
        });
      },
      onError: () => setError(true)
    }
  );

  const {
    call: fetchDataset,
    data: dataset,
    request
  } = useApi(
    initRequest(
      "stat-viewer/dataset/fetch",
      getDatasetUrl(nodeId, packageId, datasetId),
      RequestMethod.POST,
      null,
      t => ({
        onStart: t("components.statViewer.messages.fetchingDataset.start")
      }),
      undefined,
      undefined,
      statusCode => statusCode === 413 || statusCode === 414
    ),
    {
      clearOnUnmount: true,
      onSuccess: dataset => {
        if ((dataset?.id ?? []).length === 0) {
          setError(true);
        }
      },
      onError: () => setError(true)
    }
  );

  useEffect(() => {
    window.addEventListener("resize", handleStyle);
    return () => window.removeEventListener("resize", handleStyle);
  }, []);

  useEffect(() => {
    handleStyle();
  });

  const viewerIdx = getViewerIdxFromType(view?.defaultView);

  const {
    layout,
    labelFormat,
    showTrend,
    showCyclical,
    tableEmptyChar,
    chartSettings,
    mapSettings,
    detailLevel,
    invertedDims,
    timePeriodsByFreq
  } = layoutObj;

  return (
    <Box
      id={"stat-viewer"}
      sx={{
        width: "100%",
        height: "100%"
      }}
    >
      <Call cb={fetchStructure}>
        <Call
          cb={fetchDataset}
          cbParam={{
            ...request,
            data: view?.criteria ?? []
          }}
          disabled={!view}
        >
          {error ? (
            <CustomEmpty text={t("components.statViewer.errorFetchingView")} image={<ErrorIcon />} />
          ) : dataset && viewerIdx !== null ? (
            <>
              <Box id={"stat-viewer__filters"} sx={{marginBottom: "16px"}}>
                <DatasetStaticFiltersJsonStatWrapper
                  jsonStat={dataset}
                  layout={layout}
                  labelFormat={labelFormat}
                  localizedTimePeriodFormatMapExternal={localizedTimePeriodFormatMap}
                />
              </Box>
              <Box id={"stat-viewer__viewer"}>
                {viewerIdx === 0 ? (
                  <Table
                    jsonStat={dataset}
                    layout={layout}
                    labelFormat={labelFormat}
                    showTrend={showTrend}
                    showCyclical={showCyclical}
                    decimalSeparator={view.decimalSeparator}
                    roundingStrategy={view.roundingStrategy}
                    decimalPlaces={view.decimalNumber}
                    emptyChar={tableEmptyChar}
                    invertedDims={invertedDims}
                    hierarchyOnlyAttributes={nodeExtras?.HierarchyOnlyAttributes || []}
                    hideHierarchyOnlyRows={nodeExtras?.HideHierarchyOnlyRows || false}
                    filterable={true}
                    sortable={true}
                    localizedTimePeriodFormatMapExternal={localizedTimePeriodFormatMap}
                  />
                ) : viewerIdx === 1 ? (
                  <Map
                    nodeId={view.nodeId}
                    jsonStat={dataset}
                    layout={layout}
                    labelFormat={labelFormat}
                    decimalSeparator={view.decimalSeparator}
                    roundingStrategy={view.roundingStrategy}
                    decimalPlaces={view.decimalNumber}
                    defaultDetailLevel={detailLevel}
                    disableDetailLevelSelector
                    initialBaseLayer={mapSettings?.baseLayer}
                    defaultSettings={mapSettings}
                    disableSettings
                    disableBaseLayer
                    showSingleGeometry
                    localizedTimePeriodFormatMapExternal={localizedTimePeriodFormatMap}
                  />
                ) : (
                  <ChartJsonStatWrapper
                    type={view.defaultView}
                    jsonStat={dataset}
                    layout={layout}
                    timePeriodsByFreq={timePeriodsByFreq}
                    labelFormat={labelFormat}
                    showTrend={showTrend}
                    showCyclical={showCyclical}
                    decimalSeparator={view.decimalSeparator}
                    roundingStrategy={view.roundingStrategy}
                    decimalPlaces={view.decimalNumber}
                    chartSettings={chartSettings}
                    invertedDims={invertedDims}
                    localizedTimePeriodFormatMapExternal={localizedTimePeriodFormatMap}
                  />
                )}
              </Box>
            </>
          ) : null}
        </Call>
      </Call>
    </Box>
  );
};

export default StatViewer;
